/**
 * Справочник клиентских анимаций локального армянского белота (BelotArmenianLocalLobby).
 * Используется в ArmenianBelotEngineDebugPanel: вкладка «Движок» и полная JSON-сводка.
 * Не отражает онлайн-стол (OnlineBelotTable) полностью — там часть эффектов своя.
 */

/** @typedef {{
 *   id: string,
 *   title: string,
 *   trigger: string,
 *   appearance: string,
 *   purpose: string,
 *   implementation: string,
 * }} ArmenianBelotLocalUiAnimationRef */

/** @type {ArmenianBelotLocalUiAnimationRef[]} */
export const ARMENIAN_BELOT_LOCAL_UI_ANIMATIONS = [
  {
    id: 'bot_play_card_flight',
    title: 'Полёт карты бота к центру взятки',
    trigger:
      'Фаза play, ход бота: `runBotPlayCommand` в `useBelotArmenianAnimations` ставит `cardFly` и GSAP-дугу от `seatAnchorRefs[currentTurnSeat]` к `trickCenterRef`; при `prefers-reduced-motion: reduce` — мгновенный `apply(cmd)` без полёта.',
    appearance:
      'Портал `fixed` с картой: сначала рубашка (`faceDown`), по завершении таймлайна — звук flip и применение хода; дуга, лёгкий skew/rotation, «удар» по центру взятки.',
    purpose:
      'Связать ход бота с физическим местом за столом и центром взятки; не телепортировать карту.',
    implementation:
      'useBelotArmenianAnimations (`runBotPlayCommand`, `cardFly`, `flyCardWrapRef`, `pendingPlayCmdRef`), рендер в `BelotArmenianLocalLobby` (`PlayingCard` + `faceDown`).',
  },
  {
    id: 'deal_flight',
    title: 'Раздача: полёт карт от колоды к местам',
    trigger:
      'После START_ROUND, фаза торгов (auction), когда у героя появились 8 карт в руке. GSAP-полёт из deckRef к аватарам по слотам; слоты подсвечиваются по мере «приземления».',
    appearance:
      'Карты летят по дуге от центра колоды к позиции каждого игрока; у героя — по одной карте в слоты веера. Пока идёт раздача, блокируется лишний ввод.',
    purpose:
      'Визуально связать раздачу с местами за столом и с локальной «секой» (появление карт по слотам), без мгновенного появления всей руки.',
    implementation:
      'useBelotDealAnimation + GSAP; roundDealAnimActive, seatDealSlotRevealed.',
  },
  {
    id: 'auction_contract_reveal',
    title: 'Ревил контракта после торгов',
    trigger:
      'Сразу после закрытия торгов (onAuctionEnded → handleAuctionEndedReveal): один короткий полноэкранный проигрыш перед переходом к объявлениям / взяткам.',
    appearance:
      'Тёмный backdrop z≈235, «Контракт объявлен», крупные очки контракта, орб козыря с шлейфом и бликами (CSS custom properties --belot-reveal-* из позиции бейджа торгов). При множителе контры — акцентная строка ×N.',
    purpose:
      'Зафиксировать итог торгов и козырь до игры; эмоциональный акцент как «удар по столу» после bazar.',
    implementation:
      'useBelotPreGameOrchestrator (auctionRevealFx), ArmenianAuctionRevealFx.css; длительность deckTrumpRevealMs.',
  },
  {
    id: 'declare_ready_vote',
    title: 'Модалка голосования «к взяткам» (казуальный режим объявлений)',
    trigger:
      'Фаза declare, когда торги уже закрыты и нужно подтверждение всех игроков перед розыгрышем; скрывается если одновременно идёт ревил контракта или блок голосования.',
    appearance:
      'Центрированная карточка, лёгкая пульсация рамки (animate-pulse), таймер автоподтверждения, список мест Принято/Ждём.',
    purpose:
      'Синхронизировать переход к play после фазы объявлений; не столько анимация, сколько явный шлюз UX.',
    implementation:
      'Локальный JSX в BelotArmenianLocalLobby (inDeclare && …).',
  },
  {
    id: 'combo_decl_scoring_reveal',
    title: 'Всплывающий «комбо / CP» после первой взятки',
    trigger:
      'Строго на первом завершённом взятке раунда (детект по длине currentTrick 3→0 и tricksWonByTeam сумма = 1): показ очков объявлений и коротких ярлыков комбо из снимка объявлений.',
    appearance:
      'Оверлей у центра стола / счётчик CP (comboRevealFx): текстовые чипы + «+N CP», при reduced motion короче удержание.',
    purpose:
      'Показать игроку, что объявленные комбинации и очки деклараций «ожили» в матче после первого взятого.',
    implementation:
      'useBelotVisualFxQueue (COMBO_REVEAL), setComboRevealFx в ArmenianBelotTablePresentation.',
  },
  {
    id: 'bela_blot_reblot_bubble',
    title: 'Баббл Блот / Реблот у места',
    trigger:
      'При событии CARD_PLAYED в розыгрыше: если по правилам Bella на этом ходу срабатывает blot или reblot для короля/дамы козыря.',
    appearance:
      'Речевой пузырь BelotFxBubble у промежуточного места, строки «БЛОТ» / «РЕБЛОТ» и глиф масти; хвост к центру стола; гаснет по таймеру.',
    purpose:
      'Подчеркнуть редкие бонусы Блот/Реблот так, чтобы их заметили все за столом.',
    implementation:
      'onPostApplyFx → belotAdvanceBelaSpeechState, очередь BELOT_FX_PRIORITY.BELA_SPEECH, belaSpeech state.',
  },
  {
    id: 'hardcore_combo_announce_reveal',
    title: 'Вылет карт комбинации при «Объявить» (хардкор)',
    trigger:
      'Режим declarationMode hardcore, первый ход героя ещё не зафиксировал коммит объявлений; по нажатию «Объявить» у строки комбо при переходе false→true (не при повторном клике по уже включённой строке).',
    appearance:
      'Портал поверх UI (z>диалога): PlayingCard вылетают от якоря места к центру арены/взятки, собираются в веер (GSAP: вылет → веер → пауза → fade). Для **карэ** (CARRE_* / 4 карты): старт дальше за краем экрана, широкий веер с лёгким «крестом» по Y, пауза ~1.75 с, золотистый чип `belotComboLabelUi.revealChipTextFromOpts`, усиленная вспышка `box-shadow` и короткий shake корня портала (без shake при reduced-motion). Онлайн: очередь ревилов сортируется `sortDeclaredComboKeysForRevealDescending`.',
    purpose:
      'Наглядно показать состав комбо при ручном анонсе перед тем, как коммит уйдёт в движок; «Candywings»-полировка.',
    implementation:
      'useBelotComboAnnounceCardReveal (comboLabelRaw, isCarreRevealCandidate, fanSlotCarre, revealTimings(carre)), belotComboLabelUi.js; OnlineBelotTable — порядок ключей; BelotSeatSignalStrip — золотые пилюли для карэ.',
  },
  {
    id: 'declare_strip_battle_outcome',
    title: 'Полоска объявлений после ревила (кто выиграл бой комбо)',
    trigger:
      'Фаза розыгрыша: для казуала — после `DECLARATIONS_RESOLVED` с исходом; для хардкора — когда все четыре места закоммитили объявления и отработала очередь FX ревила (`waitUntilIdle` / буфер в локале).',
    appearance:
      'У проигравшей команды `BelotSeatSignalStrip` скрыт; у победившей — без изменений. При `tie` по движку пилюли у обеих команд остаются. Тот же фильтр у капли матча (`collectDeclarePillLabels` / `SeatBelotMatchDropBadge`).',
    purpose:
      'Согласовать отображение с правилом «чьи комбо считаются» после публичного ревила.',
    implementation:
      'resolveDeclarationBattleOutcome.js (compareTeams + только объявленные ключи); BelotArmenianLocalLobby / OnlineBelotTable — исход и флаг apply; BelotSeatSignalStrip — `declarationBattleOutcome` / `declarationBattleApply` / `seatTeamIndex`.',
  },
  {
    id: 'void_lead_renounce_cue',
    title: 'Сигнал «не в масть» (discard / renounce)',
    trigger:
      'После `PLAY_CARD` в розыгрыше: в `currentTrick` ≥2 карт, масть последнего хода не равна масти ведущей и это не козырь (для контракта с козырем); при «все козыри» — любой офф-лед; при б/к — любой офф-лед.',
    appearance:
      'Короткий shake места (`poker-loss-shake`, motion-safe) + иконка/подпись над местом ~0,7–0,9 с; при reduced-motion — только иконка без shake.',
    purpose:
      'Показать зрителю, что карта сыграна «вхолостую» без чтения логов движка.',
    implementation:
      'onPostApplyFx → `voidLeadCue` state → `ArmenianBelotTablePresentation` (`cardSuit` из `types`).',
  },
  {
    id: 'match_end_splash',
    title: 'Всплеск «Победа / Поражение» при завершении матча',
    trigger:
      'Переход `matchDone` (фаза `match_end`, цель по `teamScores` достигнута) при закрытой панели отладки движка.',
    appearance:
      'Поверх существующей модалки итога — крупный заголовок, лёгкий CSS-confetti (div-частицы), авто-снятие ~2,2 с; короткий звук через `play()`.',
    purpose:
      'Сильнее отметить итог матча до чтения таблицы ставок.',
    implementation:
      '`BelotArmenianLocalLobby`: `matchFxBurst` + слой внутри блока `matchDone`.',
  },
  {
    id: 'trick_collect_kapot_glow',
    title: 'Золотой акцент при сборе взятки (капот / все 8)',
    trigger:
      'Фаза `collect` в `useBelotTrickAnimations`, когда после взятки `max(tricksWonByTeam) === 8` или в контракте заявлен `kapot` и сумма взяток раунда = 8.',
    appearance:
      'Фаза 1 сбора: усиленный `box-shadow` (золотое свечение) на картах стола на время gather tween.',
    purpose:
      'Подчеркнуть редкий момент «взяли всё» без дублирования combo_reveal.',
    implementation:
      'useBelotTrickAnimations (`engineState.tricksWonByTeam`, `engineState.contract?.kapot`).',
  },
  {
    id: 'shuler_local_fx',
    title: 'Локальный режим шулера: подсветка и события',
    trigger:
      '`createInitialState({ shulerEnabled: true })` через настройку лобби; события `CARD_PLAYED_DIRTY`, `DIRTY_CAUGHT`, `DIRTY_FALSE_ACCUSE` из движка.',
    appearance:
      'Красное кольцо на месте шулера / ложного обвинителя; подсветка «грязной» карты на столе; двойной клик по подсвеченной карте — `ACCUSE_DIRTY` с места героя.',
    purpose:
      'Паритет с онлайн-семантикой без отдельного сервера; отладка и демо.',
    implementation:
      'useBelotRoundLifecycle `shulerEnabled`, чекбокс в лобби, `onPostApplyFx` + `trickUi.dirty`, `ArmenianBelotTablePresentation.onAccuseDirtyPlay`.',
  },
  {
    id: 'bot_emotes_local',
    title: 'Эмодзи-реакции над ботами',
    trigger:
      'Включён тумблер «Реакции ботов»; после хода бота (`CARD_PLAYED`, место ≠ герой) — случайный эмодзи из короткого набора.',
    appearance:
      'Крупный эмодзи над аватаром ~1,5 с; pointer-events-none.',
    purpose:
      'Лёгкая социальная окраска без связи с движком.',
    implementation:
      '`botEmoteCue` + `ArmenianBelotTablePresentation`, localStorage ключа тумблера.',
  },
  {
    id: 'trick_end_collect',
    title: 'Сбор взятки / переворот к следующей',
    trigger:
      'Завершение взятки (4 карты), переход к следующей или конец раунда — см. useBelotTrickAnimations.',
    appearance:
      'Карты стола улетают к победителю взятки / в стопку; возможны дополнительные подсказки last trick.',
    purpose:
      'Снять карты с центра и передать фокус следующему ходу без резкого исчезновения.',
    implementation:
      'useBelotTrickAnimations, trickRevealToCollectRef, GSAP.',
  },
  {
    id: 'hero_play_drag_snap',
    title: 'Перетаскивание карты хода и отскок к слоту',
    trigger:
      'Пока герой тащит карту для хода; при отмене или недопустимом дропе — отскок GSAP к исходному слоту.',
    appearance:
      'Ghost-карта следует за пальцем/мышью; snap tween при возврате.',
    purpose:
      'Тактильная обратная связь и предотвращение случайного хода.',
    implementation:
      'dragSnapTweenRef + gsap.to на ghost элемент.',
  },
  {
    id: 'match_settlement_numbers',
    title: 'Анимация чисел при списании/зачислении фишек матча',
    trigger:
      'После завершения матча при отображении дельты ставки / баланса в локальной экономике.',
    appearance:
      'Плавное изменение отображаемых чисел (requestAnimationFrame / интерполяция), без полноэкранного FX.',
    purpose:
      'Сделать финансовый итог читаемым и менее резким.',
    implementation:
      'settlementAnimRafRef в BelotArmenianLocalLobby.',
  },
];
